import { FC, useMemo, useState } from 'react';
import cn from 'classnames';

import { AccessLevel } from '@/entities/vfo/types';
import { useNotices } from '@/features/notices/useNotices';
import { PermissionGuard } from '@/features/vfo/components';
import { UserCell } from '@/features/vfo/components';
import { useCases } from '@/features/vfo/hooks/useCases';
import { useChat } from '@/features/vfo/hooks/useChat';
import { useIsFounder } from '@/features/vfo/hooks/useIsFounder';
import { useMembers } from '@/features/vfo/hooks/useMembers';
import { useAuthStore } from '@/shared/store/auth.store';
import { Icon } from '@/shared/uikit/Icon';
import { Button } from '@/shared/uikitV2/Button';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { useModalsStore } from '@/widgets/modals/modals.store';

import { ChatItem } from './ChatItem';
import {
  AvailableMembers,
  AvailableMembersHeader,
  BackButton,
  ButtonDesktop,
  ChatsList,
  ChatsWrapper,
  ChatTitle,
  Header,
  IconRotate,
  MemberList,
  Sidebar as StyledSidebar,
  SidebarContent,
} from './styled';

type ChatSidebarProps = {
  mobileView?: 'sidebar' | 'chat';
  onChatSelect?: (chatId: string) => void;
};

export const ChatSidebar: FC<ChatSidebarProps> = ({ mobileView, onChatSelect }) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'vfoChat' });

  const [isMemberListOpen, setIsMemberListOpen] = useState(false);

  const modals = useModalsStore();
  const isFounder = useIsFounder();
  const { findByChatId } = useCases();
  const { membersWithoutCurrentUser, getByProfileId } = useMembers();
  const { account } = useAuthStore();
  const notices = useNotices();

  const {
    chats,
    activeChatId,
    unreadCounts,
    hasNotificationSounds,
    markChatAsRead,
    setHasNotificationSounds,
  } = useChat();

  const handleChatClick = (chatId: string) => {
    markChatAsRead(chatId);
    onChatSelect?.(chatId);
  };

  const availableMembers = useMemo(() => {
    return membersWithoutCurrentUser.filter((item) => !item.isFounder);
  }, [membersWithoutCurrentUser]);

  return (
    <StyledSidebar className={cn({ mobileHidden: mobileView === 'chat' })}>
      <Header>
        <ChatTitle size={18} bold>
          {t('title')}
        </ChatTitle>

        <Button
          style={{ height: 'auto' }}
          type="link"
          size="small"
          icon={<Icon name={hasNotificationSounds ? 'bell' : 'bell-slash'} size="s24" />}
          onClick={() => {
            if (hasNotificationSounds) {
              notices.success(t('soundNotificationsDisabled'));
            } else {
              notices.success(t('soundNotificationsEnabled'));
            }
            setHasNotificationSounds(!hasNotificationSounds);
          }}
        />

        <BackButton
          size="small"
          type="link"
          icon={<Icon name="x" size="s20" />}
          onClick={() => modals.closeModal()}
        />
      </Header>
      <SidebarContent>
        <PermissionGuard
          role={[AccessLevel.CONNECTED, AccessLevel.DELEGATED, AccessLevel.EXECUTIVE]}
        >
          <ButtonDesktop
            size="middle"
            type="default"
            onClick={() => {
              modals.openModal({
                type: 'AddOrEditCaseModal',
                openChat: false,
              });
            }}
          >
            {t('createCaseChat')}
          </ButtonDesktop>
        </PermissionGuard>
        <ChatsWrapper>
          <ChatsList>
            {chats.map((chat) => {
              const caseData = findByChatId(chat.id);
              const member = caseData?.members.find((m) => m.profileId !== account?.id);
              const memberData =
                caseData?.isPrivateCase && member?.profileId
                  ? getByProfileId(member.profileId)
                  : undefined;

              return (
                <ChatItem
                  key={chat.id}
                  chat={chat}
                  isActive={chat.id === activeChatId}
                  onClick={() => handleChatClick(chat.id)}
                  caseData={caseData}
                  memberData={memberData}
                  unreadCount={unreadCounts[chat.id] ?? 0}
                />
              );
            })}
          </ChatsList>
        </ChatsWrapper>

        {!isFounder && (
          <PermissionGuard
            role={[AccessLevel.CONNECTED, AccessLevel.DELEGATED, AccessLevel.EXECUTIVE]}
          >
            <AvailableMembers>
              <AvailableMembersHeader onClick={() => setIsMemberListOpen(!isMemberListOpen)}>
                <span>{t('availableMembers')}</span>
                <IconRotate
                  className={cn({ active: isMemberListOpen })}
                  name="caret-down"
                  size="s20"
                />
              </AvailableMembersHeader>
              <MemberList className={cn('uikitScrollbar', { active: isMemberListOpen })}>
                {availableMembers.map((item) => (
                  <UserCell member={item} key={item.profileId} showStatus />
                ))}
              </MemberList>
            </AvailableMembers>
          </PermissionGuard>
        )}
      </SidebarContent>
    </StyledSidebar>
  );
};
